/* eslint-disable no-nested-ternary */
import React, { useCallback, useContext } from 'react';
import { makeStyles } from 'tss-react/mui';
import Button from '@ui/Button';
import Icon from '@ui/Icon';
import Tooltip from 'components/Tooltip';
import { usePromptsContext } from 'context/prompts/PromptsContext';
import { StudioClipContext } from '../StudioClip';
import { StudioClipActionProps } from './StudioClipAction';
import { instanceOfCompletedClip } from '../../../context/clips';
import LoadingIcon from '../../LoadingIndicators/CircularLoader';
import { useUserContext } from '../../../context/user/UserContext';
import { AudioFileFormat } from '../../../../server/graphql/__generated.types';
import useClipDownloader from '../../Download/useClipDownloader';
import { ActorRelatedMetadata } from '../../Download/useDownloadClips';

const StudioClipActionDownload = ({
  setDownloadError,
  project,
}: Omit<StudioClipActionProps, 'onClick'>) => {
  const { clip, srcFailedToLoad } = useContext(StudioClipContext);
  const [, dispatchPrompt] = usePromptsContext();
  const { data: userQuery } = useUserContext();
  const { onDownloadClips, downloading } = useClipDownloader({
    onDownloadStart: () => {},
    onDownloadComplete: () => {},
    setDownloadError,
  });
  const { classes } = useStyles();

  const getActorMetadata = useCallback((): ActorRelatedMetadata => {
    return {
      actorId: clip.actor.id,
      actorName: clip.actor.name,
      ttsVersion: clip.ttsVersion ?? undefined,
      actorVariantType: clip.actor.variant?.variantType?.label ?? undefined,
      actorVariantId: clip.actor.variant?.id ?? undefined,
    };
  }, [clip.actor.id, clip.actor.name, clip.actor.variant, clip.ttsVersion]);

  const handleClipDownload = useCallback(() => {
    if (userQuery?.user.settings.globalAudioFileFormatSelected) {
      onDownloadClips({
        clipIds: [clip.id],
        audioFileFormat:
          userQuery?.user.settings.globalAudioFileFormat ?? AudioFileFormat.Mp3,
        actorRelatedMetadata: getActorMetadata(),
        includeScriptFormats:
          userQuery?.user.settings.includeClipScriptFormats ?? undefined,
      });
    } else {
      dispatchPrompt({
        type: 'OpenDownloadPrompt',
        payload: {
          downloadType: 'clips',
          clipIds: [clip.id],
          projectId: project.id,
          studioVersion: project.studioVersion,
          setDownloadError,
          projectName: project.name,
        },
      });
    }
  }, [
    userQuery?.user.settings.globalAudioFileFormatSelected,
    userQuery?.user.settings.globalAudioFileFormat,
    userQuery?.user.settings.includeClipScriptFormats,
    onDownloadClips,
    clip.id,
    getActorMetadata,
    dispatchPrompt,
    project.id,
    project.studioVersion,
    project.name,
    setDownloadError,
  ]);

  if (!clip || !instanceOfCompletedClip(clip)) return null;

  const disabled =
    downloading ||
    !clip.src ||
    !!clip.failedWithError ||
    srcFailedToLoad ||
    false;

  return (
    <Tooltip text="Download" placement="bottom">
      <Button
        variant="icon"
        disabled={disabled}
        onClick={handleClipDownload}
        aria-label="download"
        className={classes.iconButton}
      >
        {downloading ? <LoadingIcon size={20} /> : <Icon type="download" />}
      </Button>
    </Tooltip>
  );
};

const useStyles = makeStyles()(theme => ({
  iconButton: {
    width: 'fit-content',
    borderRadius: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));
export default StudioClipActionDownload;
